import type { ChangeEvent } from "react";
import { UseFormReturn } from "react-hook-form";
import { CURRENCY_CODES, CURRENCY_SYMBOLS, currencySymbol } from "@/lib/format";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DollarSign, Link, Layers, X, Info } from "lucide-react";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  RadioGroup,
  RadioCard,
  RadioCardItem,
  RadioCardContent,
  RadioCardDescription,
} from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  CampaignFormData,
  TierComponent,
  PAID_COMPONENT_TYPES,
  AFFILIATE_COMPONENT_TYPES,
  ALL_COMPONENT_TYPES,
} from "./campaignFormTypes";
import CustomFormulaFields from "./CustomFormulaFields";

interface TierCardProps {
  form: UseFormReturn<CampaignFormData>;
  tierIndex: number;
  onRemove: () => void;
  isFirst: boolean;
}

const DEFAULT_COMPONENT: Record<string, TierComponent> = {
  paid: { type: "max_budget" },
  affiliate: { type: "cpa" },
  combined: { type: "max_budget" },
};

function getDefaultComponentForType(tierType: string): TierComponent[] {
  if (tierType === "combined") {
    return [
      { type: "max_budget" },
      { type: "revenue_share" },
    ];
  }
  return [DEFAULT_COMPONENT[tierType] ?? { type: "max_budget" }];
}

/** Campaign-wide currency select. Every instance binds to the same `currency`
 * form field, so changing it in one place updates every tier. */
function CurrencySelect({ form }: { form: UseFormReturn<CampaignFormData> }) {
  return (
    <FormField
      control={form.control}
      name={"currency" as any}
      render={({ field }) => (
        <Select value={field.value as string} onValueChange={field.onChange}>
          <SelectTrigger className="w-28 shrink-0">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {CURRENCY_CODES.map((code) => (
              <SelectItem key={code} value={code}>
                {CURRENCY_SYMBOLS[code]} {code}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    />
  );
}

function ComponentFields({
  form,
  tierIndex,
  compIndex,
  tierType,
}: {
  form: UseFormReturn<CampaignFormData>;
  tierIndex: number;
  compIndex: number;
  tierType: string;
}) {
  const basePath = `tiers.${tierIndex}.components.${compIndex}` as const;
  const compType = form.watch(`tiers.${tierIndex}.components.${compIndex}.type` as any);
  const symbol = currencySymbol(form.watch("currency"));

  // Determine available types based on tier type
  const availableTypes = tierType === "paid"
    ? PAID_COMPONENT_TYPES
    : tierType === "affiliate"
      ? AFFILIATE_COMPONENT_TYPES
      : ALL_COMPONENT_TYPES;

  const typeLabels: Record<string, { label: string; description: string }> = {
    max_budget: { label: "Max Budget", description: "Fixed maximum rate per creator" },
    max_cpm: { label: "Max CPM", description: "Cost per 1,000 impressions" },
    custom_formula: { label: "Custom Formula", description: "Dynamic rate via formula" },
    cpa: { label: "CPA", description: "Cost per acquisition" },
    revenue_share: { label: "Revenue Share", description: "% of sales revenue" },
    other: { label: "Other", description: "Custom compensation" },
    performance_cpm: { label: "Performance CPM", description: "Pay per view after posting" },
  };

  return (
    <div className="space-y-4">
      {/* Component type selector */}
      <FormField
        control={form.control}
        name={`${basePath}.type` as any}
        render={({ field }) => (
          <FormItem>
            {tierType !== "combined" && <FormLabel>Compensation Type</FormLabel>}
            <FormControl>
              <RadioGroup
                onValueChange={(value) => {
                  field.onChange(value);
                  // Reset component fields when type changes
                  form.setValue(`${basePath}.value` as any, undefined);
                  form.setValue(`${basePath}.description` as any, undefined);
                  form.setValue(`${basePath}.custom_formula` as any, undefined);
                  form.setValue(`${basePath}.performance_cpm` as any, undefined);
                  if (value === "custom_formula") {
                    form.setValue(`${basePath}.custom_formula` as any, {
                      expression: "",
                      constants: [],
                    });
                  }
                  if (value === "performance_cpm") {
                    // Default: $10 CPM on views above the creator's median.
                    form.setValue(`${basePath}.performance_cpm` as any, {
                      boundary_mode: "median_multiple",
                      rates: [{ above: 1, cpm: 10 }],
                      cap: undefined,
                      cap_mode: "absolute",
                      window_months: 1,
                    });
                  }
                }}
                value={field.value as string}
                className="lg:grid-cols-2"
              >
                {availableTypes.map((t) => (
                  <RadioCard key={t} className="gap-3 p-3">
                    <RadioCardContent className="gap-0.5">
                      <Label htmlFor={`${basePath}-${t}`}>
                        {typeLabels[t]?.label ?? t}
                      </Label>
                      <RadioCardDescription>
                        {typeLabels[t]?.description}
                      </RadioCardDescription>
                    </RadioCardContent>
                    <RadioCardItem value={t} id={`${basePath}-${t}`} />
                  </RadioCard>
                ))}
              </RadioGroup>
            </FormControl>
            <FormMessage className="text-xs" />
          </FormItem>
        )}
      />

      {/* Value inputs based on component type */}
      {compType === "max_budget" && (
        <FormField
          control={form.control}
          name={`${basePath}.value` as any}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Max budget per creator</FormLabel>
              <div className="flex flex-row gap-2">
                <FormControl>
                  <Input
                    placeholder="e.g. 1000"
                    value={field.value ?? ""}
                    type="text"
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, "");
                      field.onChange(value === "" ? undefined : Number(value));
                    }}
                  />
                </FormControl>
                <CurrencySelect form={form} />
              </div>
              <FormDescription>
                Maximum budget you are willing to pay per creator (in {symbol}).
              </FormDescription>
              <FormMessage className="text-xs mt-0" />
            </FormItem>
          )}
        />
      )}

      {compType === "max_cpm" && (
        <div className="flex flex-row items-center gap-2">
          <FormField
            control={form.control}
            name={`${basePath}.value` as any}
            render={({ field }) => (
              <FormItem className="w-full">
                <FormLabel>Max CPM per creator</FormLabel>
                <div className="flex flex-row gap-2">
                  <FormControl>
                    <Input
                      placeholder="e.g. 20"
                      value={field.value ?? ""}
                      type="text"
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, "");
                        field.onChange(value === "" ? undefined : Number(value));
                      }}
                    />
                  </FormControl>
                  <CurrencySelect form={form} />
                </div>
                <FormDescription>
                  Max cost per 1,000 impressions per creator (in {symbol}).
                </FormDescription>
                <FormMessage className="text-xs mt-0" />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="number_of_deliverables"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormLabel>Number of deliverables</FormLabel>
                <FormControl>
                  <Input
                    placeholder="e.g. 1"
                    value={field.value ?? ""}
                    type="number"
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, "");
                      field.onChange(value === "" ? undefined : Number(value));
                    }}
                  />
                </FormControl>
                <FormDescription>
                  Total pieces of content per creator. Amy multiplies the CPM budget by this count.
                </FormDescription>
                <FormMessage className="text-xs mt-0" />
              </FormItem>
            )}
          />
        </div>
      )}

      {compType === "custom_formula" && (
        <>
          <div className="space-y-2">
            <Label>Currency</Label>
            <CurrencySelect form={form} />
            <p className="text-xs text-muted-foreground">
              The formula result is denominated in this currency (applies to the
              whole campaign).
            </p>
          </div>
          <CustomFormulaFields
            form={form}
            basePath={`${basePath}.custom_formula`}
          />
        </>
      )}

      {compType === "cpa" && (
        <>
          <FormField
            control={form.control}
            name={`${basePath}.value` as any}
            render={({ field }) => (
              <FormItem>
                <FormLabel>CPA Amount</FormLabel>
                <div className="flex flex-row gap-2">
                  <FormControl>
                    <Input
                      placeholder="e.g. 500"
                      value={field.value ?? ""}
                      type="text"
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, "");
                        field.onChange(value === "" ? undefined : Number(value));
                      }}
                    />
                  </FormControl>
                  <CurrencySelect form={form} />
                </div>
                <FormDescription>
                  Amount paid per acquisition/conversion (in {symbol}).
                </FormDescription>
                <FormMessage className="text-xs mt-0" />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={`${basePath}.description` as any}
            render={({ field }) => (
              <FormItem>
                <FormLabel>CPA Description</FormLabel>
                <FormControl>
                  <Input
                    placeholder="e.g. Per completed purchase, Per app install"
                    {...field}
                    value={(field.value as string) ?? ""}
                  />
                </FormControl>
                <FormDescription>
                  Describe what counts as an acquisition.
                </FormDescription>
                <FormMessage className="text-xs mt-0" />
              </FormItem>
            )}
          />
        </>
      )}

      {compType === "revenue_share" && (
        <FormField
          control={form.control}
          name={`${basePath}.value` as any}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Revenue Share Percentage</FormLabel>
              <FormControl>
                <Input
                  placeholder="e.g. 20"
                  value={field.value ?? ""}
                  type="text"
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, "");
                    field.onChange(value === "" ? undefined : Number(value));
                  }}
                />
              </FormControl>
              <FormDescription>
                Percentage of revenue shared with creators.
              </FormDescription>
              <FormMessage className="text-xs mt-0" />
            </FormItem>
          )}
        />
      )}

      {compType === "other" && (
        <FormField
          control={form.control}
          name={`${basePath}.description` as any}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Compensation Details</FormLabel>
              <FormControl>
                <Textarea
                  className="min-h-24"
                  placeholder="e.g. Tiered CPA: $20 first 100 sales, $15 next 100, $10 thereafter"
                  {...field}
                  value={(field.value as string) ?? ""}
                />
              </FormControl>
              <FormDescription>
                Describe your custom compensation structure.
              </FormDescription>
              <FormMessage className="text-xs mt-0" />
            </FormItem>
          )}
        />
      )}

      {compType === "performance_cpm" && (
        <PerformanceCpmFields form={form} basePath={basePath} symbol={symbol} />
      )}

      {/* Info alerts */}
      {compType === "max_cpm" && (
        <Alert variant="info">
          <AlertTitle>
            <Info /> How Amy handles CPM negotiations
          </AlertTitle>
          <AlertDescription>
            Amy calculates the max budget per creator as CPM × median views × deliverables ÷ 1000, then negotiates within that limit.
          </AlertDescription>
        </Alert>
      )}

      {compType === "custom_formula" && (
        <Alert variant="info">
          <AlertTitle>
            <Info /> How Amy uses your formula
          </AlertTitle>
          <AlertDescription>
            Amy collects required numeric fields from creators during negotiation, then evaluates your formula to determine the max budget and negotiates within that limit.
          </AlertDescription>
        </Alert>
      )}

      {compType === "performance_cpm" && (
        <Alert variant="info">
          <AlertTitle>
            <Info /> How performance CPM pays out
          </AlertTitle>
          <AlertDescription>
            Brackets are marginal (like tax brackets): each CPM pays only the views inside its slice, per video. Median-relative floors are resolved into exact view counts for each creator when the deal closes. Views are tracked by campaign analytics; once the measurement window passes, the payment is calculated and appears as a task for your team to pay out.
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}

function PerformanceCpmFields({
  form,
  basePath,
  symbol,
}: {
  form: UseFormReturn<CampaignFormData>;
  basePath: string;
  symbol: string;
}) {
  const cfgPath = `${basePath}.performance_cpm`;
  const boundaryMode = form.watch(`${cfgPath}.boundary_mode` as any) as string;
  const capMode = form.watch(`${cfgPath}.cap_mode` as any) as string;
  const rates = (form.watch(`${cfgPath}.rates` as any) as { above?: number; cpm?: number }[]) ?? [];
  const isMedian = boundaryMode === "median_multiple";
  const isMedianCap = capMode === "median_cpm";

  const numberChange =
    (onChange: (v: number | undefined) => void) =>
    (e: ChangeEvent<HTMLInputElement>) => {
      const v = e.target.valueAsNumber;
      onChange(Number.isNaN(v) ? undefined : v);
    };

  return (
    <div className="space-y-4">
      {/* Boundary mode */}
      <FormField
        control={form.control}
        name={`${cfgPath}.boundary_mode` as any}
        render={({ field }) => (
          <FormItem>
            <FormLabel>View thresholds are…</FormLabel>
            <FormControl>
              <RadioGroup
                onValueChange={field.onChange}
                value={field.value as string}
                className="grid grid-cols-2 gap-2"
              >
                <RadioCard className="gap-3 p-3">
                  <RadioCardContent className="gap-0.5">
                    <Label htmlFor={`${cfgPath}-median`}>× Creator's median</Label>
                    <RadioCardDescription>
                      Floors are multiples of each creator's median views (1 = their median)
                    </RadioCardDescription>
                  </RadioCardContent>
                  <RadioCardItem value="median_multiple" id={`${cfgPath}-median`} />
                </RadioCard>
                <RadioCard className="gap-3 p-3">
                  <RadioCardContent className="gap-0.5">
                    <Label htmlFor={`${cfgPath}-absolute`}>Absolute views</Label>
                    <RadioCardDescription>
                      Floors are fixed view counts, the same for every creator
                    </RadioCardDescription>
                  </RadioCardContent>
                  <RadioCardItem value="absolute" id={`${cfgPath}-absolute`} />
                </RadioCard>
              </RadioGroup>
            </FormControl>
            <FormMessage className="text-xs" />
          </FormItem>
        )}
      />

      {/* CPM brackets */}
      <div className="space-y-2">
        <Label>CPM brackets</Label>
        {rates.map((_, rateIndex) => (
          <div key={rateIndex} className="flex flex-row items-start gap-2">
            <FormField
              control={form.control}
              name={`${cfgPath}.rates.${rateIndex}.above` as any}
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormControl>
                    <Input
                      type="number"
                      min={0}
                      step={isMedian ? "0.01" : "1"}
                      placeholder={isMedian ? "e.g. 0.33" : "e.g. 30000"}
                      value={(field.value as number | undefined) ?? ""}
                      onChange={numberChange(field.onChange)}
                    />
                  </FormControl>
                  <FormDescription>
                    Views above {isMedian ? "(× median)" : "(view count)"}
                  </FormDescription>
                  <FormMessage className="text-xs mt-0" />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name={`${cfgPath}.rates.${rateIndex}.cpm` as any}
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormControl>
                    <Input
                      type="number"
                      min={0}
                      step="0.01"
                      placeholder="e.g. 10"
                      value={(field.value as number | undefined) ?? ""}
                      onChange={numberChange(field.onChange)}
                    />
                  </FormControl>
                  <FormDescription>CPM ({symbol} per 1,000 views)</FormDescription>
                  <FormMessage className="text-xs mt-0" />
                </FormItem>
              )}
            />
            {rates.length > 1 && (
              <Button
                type="button"
                variant="destructive-ghost"
                size="icon-sm"
                className="mt-1 shrink-0"
                onClick={() =>
                  form.setValue(
                    `${cfgPath}.rates` as any,
                    rates.filter((_, i) => i !== rateIndex),
                    { shouldValidate: true }
                  )
                }
              >
                <X className="size-4" />
              </Button>
            )}
          </div>
        ))}
        {rates.length < 3 && (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() =>
              form.setValue(`${cfgPath}.rates` as any, [...rates, { above: undefined, cpm: undefined }], {
                shouldValidate: false,
              })
            }
          >
            Add bracket
          </Button>
        )}
      </div>

      {/* Cap mode */}
      <FormField
        control={form.control}
        name={`${cfgPath}.cap_mode` as any}
        render={({ field }) => (
          <FormItem>
            <FormLabel>Max payout per creator is…</FormLabel>
            <FormControl>
              <RadioGroup
                onValueChange={field.onChange}
                value={(field.value as string) ?? "absolute"}
                className="grid grid-cols-2 gap-2"
              >
                <RadioCard className="gap-3 p-3">
                  <RadioCardContent className="gap-0.5">
                    <Label htmlFor={`${cfgPath}-cap-fixed`}>Fixed amount</Label>
                    <RadioCardDescription>
                      One hard {symbol} ceiling, the same for every creator
                    </RadioCardDescription>
                  </RadioCardContent>
                  <RadioCardItem value="absolute" id={`${cfgPath}-cap-fixed`} />
                </RadioCard>
                <RadioCard className="gap-3 p-3">
                  <RadioCardContent className="gap-0.5">
                    <Label htmlFor={`${cfgPath}-cap-median`}>CPM × their median</Label>
                    <RadioCardDescription>
                      Scales with creator size: median views × CPM ÷ 1,000
                    </RadioCardDescription>
                  </RadioCardContent>
                  <RadioCardItem value="median_cpm" id={`${cfgPath}-cap-median`} />
                </RadioCard>
              </RadioGroup>
            </FormControl>
            <FormMessage className="text-xs" />
          </FormItem>
        )}
      />

      {/* Cap + window */}
      <div className="flex flex-row items-start gap-2">
        <FormField
          control={form.control}
          name={`${cfgPath}.cap` as any}
          render={({ field }) => (
            <FormItem className="w-full">
              <FormLabel>{isMedianCap ? "Cap CPM" : "Max payout per creator"}</FormLabel>
              <div className="flex flex-row gap-2">
                <FormControl>
                  <Input
                    type="number"
                    min={0}
                    step={isMedianCap ? "0.01" : "1"}
                    placeholder={isMedianCap ? "e.g. 50" : "e.g. 2500"}
                    value={(field.value as number | undefined) ?? ""}
                    onChange={numberChange(field.onChange)}
                  />
                </FormControl>
                <CurrencySelect form={form} />
              </div>
              <FormDescription>
                {isMedianCap
                  ? `Each creator's ceiling = their median views × this CPM ÷ 1,000 (50k median × ${symbol}50 → ${symbol}2,500). Protects you from a viral outlier at every creator size.`
                  : `Hard ceiling on the performance payout (in ${symbol}) — protects you from a viral outlier.`}
              </FormDescription>
              <FormMessage className="text-xs mt-0" />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name={`${cfgPath}.window_months` as any}
          render={({ field }) => (
            <FormItem className="w-full">
              <FormLabel>Measurement window (months)</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  min={1}
                  max={24}
                  step="1"
                  placeholder="1"
                  value={(field.value as number | undefined) ?? ""}
                  onChange={numberChange(field.onChange)}
                />
              </FormControl>
              <FormDescription>
                Views are measured this long after each video is posted.
              </FormDescription>
              <FormMessage className="text-xs mt-0" />
            </FormItem>
          )}
        />
      </div>

      {/* Guaranteed minimum — stored as a fraction of the cap, entered as %. */}
      <FormField
        control={form.control}
        name={`${cfgPath}.flat_share` as any}
        render={({ field }) => (
          <FormItem>
            <FormLabel>Guaranteed minimum — % of the cap (optional)</FormLabel>
            <FormControl>
              <Input
                type="number"
                min={0}
                max={99}
                step="0.01"
                placeholder="e.g. 33.33 for one third"
                value={
                  typeof field.value === "number"
                    ? Math.round(field.value * 10000) / 100
                    : ""
                }
                onChange={(e) => {
                  const v = e.target.valueAsNumber;
                  field.onChange(Number.isNaN(v) || v <= 0 ? undefined : v / 100);
                }}
              />
            </FormControl>
            <FormDescription>
              Floors the payout at this share of the cap — the flat fee is derived per creator, never hand-computed
              from their median. Example: {symbol}30 CPM cap on a 10k median → {symbol}300 cap; 33.33% → {symbol}100
              guaranteed, and the payout is clamp(CPM × actual views ÷ 1,000, {symbol}100, {symbol}300). Leave empty
              for no guarantee.
            </FormDescription>
            <FormMessage className="text-xs mt-0" />
          </FormItem>
        )}
      />
    </div>
  );
}

export default function TierCard({ form, tierIndex, onRemove, isFirst }: TierCardProps) {
  const tierType = form.watch(`tiers.${tierIndex}.tier_type` as any) as string;

  return (
    <div className="rounded-lg border bg-background p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold">Tier {tierIndex + 1}</span>
          <Badge variant={isFirst ? "default" : "secondary"}>
            {isFirst ? "Primary" : "Fallback"}
          </Badge>
        </div>
        {!isFirst && (
          <Button
            type="button"
            variant="destructive-ghost"
            size="icon-sm"
            onClick={onRemove}
          >
            <X className="size-4" />
          </Button>
        )}
      </div>

      {!isFirst && (
        <p className="text-xs text-muted-foreground -mt-2">
          Offered if the creator declines the previous tier.
        </p>
      )}

      {/* Tier Type Selector */}
      <FormField
        control={form.control}
        name={`tiers.${tierIndex}.tier_type` as any}
        render={({ field }) => (
          <FormItem>
            <FormLabel>Partnership Type</FormLabel>
            <FormControl>
              <RadioGroup
                onValueChange={(value) => {
                  field.onChange(value);
                  // Reset components for new tier type
                  const newComponents = getDefaultComponentForType(value);
                  form.setValue(`tiers.${tierIndex}.components` as any, newComponents);
                  form.setValue(
                    `tiers.${tierIndex}.operator` as any,
                    value === "combined" ? "and" : null
                  );
                }}
                value={field.value as string}
                className="grid grid-cols-3 lg:grid-cols-3 gap-2"
              >
                <RadioCard className="items-center gap-2 p-3">
                  <DollarSign />
                  <Label htmlFor={`tier-${tierIndex}-paid`} className="text-sm">
                    Paid
                  </Label>
                  <RadioCardItem value="paid" id={`tier-${tierIndex}-paid`} />
                </RadioCard>
                <RadioCard className="items-center gap-2 p-3">
                  <Link />
                  <Label htmlFor={`tier-${tierIndex}-affiliate`} className="text-sm">
                    Affiliate
                  </Label>
                  <RadioCardItem value="affiliate" id={`tier-${tierIndex}-affiliate`} />
                </RadioCard>
                <RadioCard className="items-center gap-2 p-3">
                  <Layers />
                  <Label htmlFor={`tier-${tierIndex}-combined`} className="text-sm">
                    Combined
                  </Label>
                  <RadioCardItem value="combined" id={`tier-${tierIndex}-combined`} />
                </RadioCard>
              </RadioGroup>
            </FormControl>
            <FormMessage className="text-xs" />
          </FormItem>
        )}
      />

      {/* Component fields based on tier type */}
      {tierType === "combined" ? (
        <div className="space-y-4">
          <div className="rounded-md border border-dashed p-3 space-y-4">
            <span className="text-xs font-medium text-muted-foreground">Component 1</span>
            <ComponentFields
              form={form}
              tierIndex={tierIndex}
              compIndex={0}
              tierType={tierType}
            />
          </div>

          <div className="flex items-center justify-center">
            <Badge variant="outline" className="text-xs uppercase tracking-wider">
              and
            </Badge>
          </div>

          <div className="rounded-md border border-dashed p-3 space-y-4">
            <span className="text-xs font-medium text-muted-foreground">Component 2</span>
            <ComponentFields
              form={form}
              tierIndex={tierIndex}
              compIndex={1}
              tierType={tierType}
            />
          </div>
        </div>
      ) : (
        <ComponentFields
          form={form}
          tierIndex={tierIndex}
          compIndex={0}
          tierType={tierType}
        />
      )}
    </div>
  );
}
